import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Ticket } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { createTicketFromSession } from '../../services/api/tickets';
import { getApiErrorDetails } from '../../utils/api-error';
import { cn } from '../../utils/cn';

import { ticketTabParams } from './sessions-tab';

interface Props {
  sessionId: string;
}

export const TicketFromSessionButton = ({ sessionId }: Props) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<
    { isError: false; message: string; ticketId: string } | { isError: true; message: string } | null
  >(null);

  const mutation = useMutation({
    mutationFn: () => createTicketFromSession(sessionId, note.trim() || undefined),
    onSuccess: ({ ticket }) => {
      setResult({ message: `Utkast skapat: ”${ticket.title}”`, isError: false, ticketId: ticket.id });
      setOpen(false);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets-count-draft'] });
    },
    onError: (error: unknown) => {
      setResult({
        message: `Kunde inte skapa ticket — ${getApiErrorDetails(error)}`,
        isError: true,
      });
    },
  });

  if (result && !result.isError) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
        <Ticket className="h-4 w-4 flex-shrink-0" />
        <span>{result.message}</span>
        <button
          type="button"
          onClick={() => setSearchParams(ticketTabParams(searchParams, result.ticketId))}
          className="font-medium underline hover:no-underline"
        >
          Öppna Tickets-fliken
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm text-indigo-700 hover:text-indigo-900 hover:underline transition"
        >
          <Ticket className="h-4 w-4" /> Skapa ticket av denna session
        </button>
      ) : (
        <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
          <p className="text-xs text-slate-600">
            Analyserar sessionen och skriver ett Claude-färdigt ticket-utkast till kön i
            Tickets-fliken. Du godkänner eller avvisar det där.
          </p>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Vad gick fel? (valfri kontext till ticketskrivaren)"
            maxLength={2000}
            disabled={mutation.isPending}
            className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-slate-100"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
              className={cn(
                'flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition',
                'hover:bg-indigo-500 disabled:bg-slate-300',
              )}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Skapar ticket… kan ta en minut
                </>
              ) : (
                <>
                  <Ticket className="h-4 w-4" /> Skapa utkast
                </>
              )}
            </button>
            {!mutation.isPending && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setResult(null);
                }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Avbryt
              </button>
            )}
          </div>
          {result?.isError && <p className="text-sm text-red-700">{result.message}</p>}
        </div>
      )}
    </div>
  );
};
